import { MultiselectProps } from "@amzn/awsui-components-react/polaris/multiselect";
import { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import {
  GetTeamConfigResponse,
  TeamConfigColumn,
  TeamConfigState,
} from "services/adi/types";

/** The ADI data domain behind the TAR (Canada) create page. */
export const TAR_DOMAIN = "ca-intltdw";

/**
 * Layers a business-logic `*ByScope` map: the domains' entries first, in the
 * order given, then the country's entries unioned on top. An entry whose key
 * is already present replaces it in place (a country entry overrides the
 * domain's); a new key is appended.
 */
const layerByScope = <T>(
  byScope: Record<string, T[]> | undefined,
  domains: readonly string[],
  country: string | undefined,
  keyOf: (item: T) => string,
): T[] => {
  const items: T[] = [];
  const indexByKey = new Map<string, number>();
  for (const scope of country ? [...domains, country] : domains) {
    for (const item of byScope?.[scope] || []) {
      const key = keyOf(item);
      const i = indexByKey.get(key);
      if (i === undefined) {
        indexByKey.set(key, items.length);
        items.push(item);
      } else {
        items[i] = item;
      }
    }
  }
  return items;
};

/** The scope's columns: domains first, then the country's, keyed by name. */
export const scopedColumns = (
  teamConfig: GetTeamConfigResponse | undefined,
  domains: readonly string[],
  country: string | undefined,
): TeamConfigColumn[] =>
  layerByScope(teamConfig?.columnsByScope, domains, country, (c) => c.name);

/** The scope's tax types: domains first, then the country's, without repeats. */
export const scopedTaxTypes = (
  teamConfig: GetTeamConfigResponse | undefined,
  domains: readonly string[],
  country: string | undefined,
): string[] =>
  layerByScope(teamConfig?.taxTypesByScope, domains, country, (t) => t);

/**
 * The team's domains that cover `country`, from the team config's `domains`
 * and `countriesByScope`, in the team's order.
 */
export const domainsForCountry = (
  teamConfig: GetTeamConfigResponse | undefined,
  country: string | undefined,
): string[] =>
  country
    ? (teamConfig?.domains || []).filter((domain) =>
        teamConfig?.countriesByScope?.[domain]?.includes(country),
      )
    : [];

/**
 * Builds the State/Province options for one country of a scoped ADI team.
 *
 * The base list is the Gaia global config's government entities for the
 * country (ADI `code`/`name`/`group` map to Gaia `value`/`label`/`labelTag`).
 * States layer by country first, then by domain: the country's
 * excluded/additional states apply on the base, then each domain's apply on
 * top, in the order given. Exclusions drop a code; an addition replaces the
 * entry with the same code, otherwise it is appended.
 *
 * Options are grouped by `group`, groups in first-seen order and options
 * sorted by label within a group, as Gaia does for government entities.
 */
export const buildScopedStateOptions = (
  governmentEntityOptions: ReadonlyArray<
    SelectProps.Option | SelectProps.OptionGroup
  >,
  country: string | undefined,
  teamConfig: GetTeamConfigResponse | undefined,
  domains: readonly string[],
): MultiselectProps.Options => {
  if (!country || !teamConfig) return [];

  const states: TeamConfigState[] = governmentEntityOptions.flatMap((group) =>
    "options" in group
      ? group.options
          .filter(
            (option) =>
              (option as { country?: string }).country === country &&
              option.value,
          )
          .map((option) => ({
            code: option.value!,
            name: option.label || option.value!,
            group: group.label,
          }))
      : [],
  );

  for (const scope of [country, ...domains]) {
    const excluded = new Set(
      (teamConfig.excludedStatesByScope?.[scope] || []).map((s) => s.code),
    );
    for (let i = states.length - 1; i >= 0; i--) {
      if (excluded.has(states[i].code)) states.splice(i, 1);
    }
    for (const added of teamConfig.additionalStatesByScope?.[scope] || []) {
      const i = states.findIndex((s) => s.code === added.code);
      if (i === -1) states.push(added);
      else states[i] = added;
    }
  }

  const groups = new Map<string, MultiselectProps.Option[]>();
  for (const state of states) {
    const group = state.group || "";
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push({ label: state.name, value: state.code });
  }
  return Array.from(groups, ([label, options]) => ({
    label,
    options: options.sort((a, b) => (a.label! > b.label! ? 1 : -1)),
  }));
};

/** Flattens grouped Multiselect options into their leaf options. */
export const flattenOptions = (
  options: MultiselectProps.Options,
): MultiselectProps.Option[] =>
  options.flatMap((option) =>
    "options" in option ? [...option.options] : [option],
  );
